import { useEffect, useState, useRef } from 'react';
import api from '../api/api';

const cache = {};

export default function useCropTypes(year) {
  const [cropTypes, setCropTypes] = useState([]);
  const abortController = useRef();

  useEffect(() => {
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    (async () => {
      if (cache[year]) {
        setCropTypes(cache[year]);
      } else {
        const { signal } = abortController.current;
        const res = await api().fetchCropTypes(year, signal);
        cache[year] = parseCropTypes(year, res.data);
        setCropTypes(cache[year]);
      }
    })();

    return () => {
      abortController.current.abort();
    };
  }, [year]);

  function getCropNameById(cropTypeId) {
    const crops = cropTypes.length ? cropTypes : cache[year] ?? [];
    const crop = crops.find((cropType) => cropType.id === cropTypeId && !cropType.predefined);
    return crop?.names.de.toLowerCase();
  }

  function getCropTypeNameByCode(cropTypeCode) {
    const crops = cropTypes.length ? cropTypes : cache[year] ?? [];
    const crop = crops.find((cropType) => cropType.code === cropTypeCode && !cropType.predefined);
    return crop?.names.de.toLowerCase();
  }

  return { cropTypes, getCropNameById, getCropTypeNameByCode };
}

export const CROP_TYPE_CODES = {
  ALL: 'All',
};

function parseCropTypes(year, cropTypes) {
  const newCropTypes = cropTypes.sort((a, b) => a.names.de.localeCompare(b.names.de));

  const predefinedCrops = [
    { id: '105', name: 'KÖRNERMAIS' },
    { id: '109', name: 'SILOMAIS' },
    { id: '110', name: 'WINTERGERSTE' },
    { id: '111', name: 'SOMMERGERSTE' },
    { id: '112', name: 'WINTERHAFER' },
    { id: '116', name: 'WINTERTRITICALE' },
    { id: '117', name: 'SORGHUM' },
    { id: '137', name: 'SOMMERWEICHWEIZEN' },
    { id: '138', name: 'WINTERWEICHWEIZEN' },
    { id: '142', name: 'WINTERROGGEN' },
    { id: '155', name: 'SOMMERHAFER' },
    { id: '165', name: 'WINTERHARTWEIZEN (DURUM)' },
    { id: '166', name: 'SOMMERHARTWEIZEN (DURUM)' },
    { id: '201', name: 'KÖRNERERBSEN' },
    { id: '301', name: 'WINTERRAPS' },
    { id: '302', name: 'SOMMERRAPS' },
    { id: '307', name: 'SONNENBLUMEN' },
    { id: '308', name: 'SOJABOHNEN' },
    { id: '513', name: 'FRÜHKARTOFFELN' },
    { id: '525', name: 'SPEISEKARTOFFELN' },
    { id: '519', name: 'STÄRKEINDUSTRIEKARTOFFELN' },
    { id: '634', name: 'WEIZEN' },
    { id: '635', name: 'LUZERNE' },
    { id: '651', name: 'ZUCKERRÜBEN' },
    { id: '751', name: 'ÖLKÜRBIS' },
  ];

  const predefinedCropsOptions = predefinedCrops.map((crop) => {
    return {
      id: `${year}.CL_CROP.${crop.id}`,
      names: {
        de: ' -- ' + crop.name,
      },
      code: crop.id,
      scope: year,
      status: 'ACTIVE',
      predefined: true,
    };
  });

  const allCropsOption = {
    id: `${year}.CL_CROP.${CROP_TYPE_CODES.ALL.toUpperCase()}`,
    names: {
      de: 'Alle Fruchtarten',
    },
    code: CROP_TYPE_CODES.ALL,
    scope: year,
    status: 'ACTIVE',
  };

  return [allCropsOption, ...predefinedCropsOptions, ...newCropTypes];
}
